// Get New Leads (Lead Marketplace).
//
// Role-aware:
//   - Super-admin (agency): full KPI dashboard — volume, fill rate, avg bid,
//     revenue-share, top buyers, by-vertical/zip/category, daily volume series
//   - Sub-account (or ghosted super-admin): buyer profile + enrollment +
//     delivery history — this is where sub-account owners set what leads
//     they want and see what they've received
import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Spinner, EmptyState } from '../ui.jsx';

const ALL_VERTICALS = ['HVAC','Plumbing','Electrical','Roofing','Garage Doors','Handyman','Landscaping','Pest Control','Cleaning','Insurance','Real Estate','Other'];
// Sub-account operators only see verticals relevant to their trade. Agency
// view (super-admin, not ghosted) always sees the full list.
function visibleVerticals(accountIndustry) {
  if (!accountIndustry) return ALL_VERTICALS;
  const canon = String(accountIndustry).toLowerCase();
  const match = ALL_VERTICALS.find(v => v.toLowerCase() === canon || v.toLowerCase().replace(/\s+/g,'') === canon);
  return match ? [match] : ALL_VERTICALS;
}

const PRICING_MODELS = [
  { id:'exclusive',   label:'Exclusive',       desc:'Sole recipient. Full price, best speed-to-lead.' },
  { id:'shared',      label:'Shared',          desc:'Up to 3 buyers get it. Cheaper.' },
  { id:'per_booked',  label:'Per Booked Appt', desc:'Pay only if the lead becomes a booked appointment.' },
  { id:'per_revenue', label:'Per Revenue',     desc:'No upfront fee. Pay % of closed revenue.' },
];

export function LeadMarketplace({ user, toast }) {
  // Agency view when super_admin is on their own plane (not ghosted). Sub-
  // account owners AND ghosted super-admins both see the buyer view.
  const isAgencyView = user?.role === 'super_admin' && !user?.accountId;
  return isAgencyView
    ? <AgencyKPIsView toast={toast}/>
    : <SubAccountBuyerView toast={toast} accountIndustry={user?.account?.industry}/>;
}

// ─── AGENCY VIEW ───────────────────────────────────────────────────────────
function AgencyKPIsView({ toast }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    setData(null);
    api(`/api/lead-marketplace/dashboard?days=${days}`).then(setData).catch(e=>toast(e.message,'error'));
  }, [days]);
  if (!data) return <Spinner sz={32}/>;

  const t = data.totals || {};
  const trend = data.trend || {};
  const fr = data.fillRate || {};
  const pr = data.pricing || {};
  const ttf = data.timeToFill || {};

  return <div style={{ padding:'20px 24px', maxWidth:1300 }}>
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
      <div style={{ flex:1 }}>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>💰 Lead Marketplace — Agency KPIs</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
          Volume, fill rate, avg bid, revenue share, and by-vertical/zip breakdowns. Sub-accounts see their own buyer view.
        </div>
      </div>
      <Select value={String(days)} onChange={e=>setDays(Number(e.target.value))} style={{ width:130 }}>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
      </Select>
      <Btn onClick={()=>window.open('/marketplace-apply', '_blank')}>🔗 Buyer Signup URL</Btn>
    </div>

    {/* Primary KPI row */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:10, marginBottom:14 }}>
      <Kpi label="Lead Volume" value={t.leads||0} deltaPct={trend.leadsPctChange} color={G.cyan}/>
      <Kpi label="Deliveries" value={t.deliveries||0} sub={`${(t.activeBuyers||0)} active buyers`} color={G.purple}/>
      <Kpi label="Gross Revenue" value={fmtUsd((t.grossRevenueCents||0)/100)} deltaPct={trend.revenuePctChange} color={G.green}/>
      <Kpi label="Rev-Share Billed" value={fmtUsd((t.revenueShareBilledCents||0)/100)} sub="From sub-account wins" color={G.green}/>
      <Kpi label="Fill Rate" value={`${fr.pct||0}%`} sub={`${fr.filled||0}/${fr.routed||0} routed`} color={fr.pct>=80?G.green:fr.pct>=50?G.yellow:G.red}/>
      <Kpi label="Avg Time-to-Fill" value={ttf.avgMinutes ? `${ttf.avgMinutes}m` : '—'} sub="Route → first buyer" color={G.orange}/>
    </div>

    {/* Pricing row */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:10, marginBottom:14 }}>
      <Kpi label="Avg Max Bid" value={fmtUsd((pr.avgMaxBidCents||0)/100)} sub={`Max: ${fmtUsd((pr.maxMaxBidCents||0)/100)}`} color={G.blue}/>
      <Kpi label="Avg Flat Price" value={fmtUsd((pr.avgFlatPriceCents||0)/100)} sub="External buyers" color={G.blue}/>
      <Kpi label="Avg Realized Charge" value={fmtUsd((pr.avgRealizedChargeCents||0)/100)} sub={`Max: ${fmtUsd((pr.maxRealizedChargeCents||0)/100)}`} color={G.green}/>
      <Kpi label="Refunds" value={t.refundsCount||0} sub={fmtUsd((t.refundedCents||0)/100)} color={(t.refundsCount||0)>0?G.red:G.textMuted}/>
    </div>

    {/* Daily volume mini-chart */}
    <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ fontWeight:700 }}>Daily Lead Volume</div>
        <div style={{ fontSize:12, color:G.textMuted }}>{(data.daily||[]).length} days with activity</div>
      </div>
      <MiniBarChart data={data.daily||[]}/>
    </div>

    {/* Breakdowns row */}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
      <BreakdownCard title="By Vertical" rows={(data.byVertical||[]).map(r => ({ label:r.vertical, value:r.n }))}/>
      <BreakdownCard title="By Category" rows={(data.byCategory||[]).map(r => ({ label:r.category||'—', value:r.n, right: fmtUsd((r.base_price_total_cents||0)/100) }))}/>
      <BreakdownCard title="Top Zips" rows={(data.byZip||[]).map(r => ({ label:r.zip, value:r.n }))}/>
    </div>

    {/* Top buyers */}
    <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
      <div style={{ fontWeight:700, marginBottom:10 }}>Top Buyers</div>
      {!(data.topBuyers||[]).length ? <div style={{ color:G.textMuted, fontSize:13 }}>No buyer activity yet.</div> :
        <table style={{ width:'100%', fontSize:13 }}>
          <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Buyer</th>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Type</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Deliveries</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Won</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Avg Response</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Spent</th>
          </tr></thead>
          <tbody>
            {(data.topBuyers||[]).map(b => (
              <tr key={b.id} style={{ borderTop:`1px solid ${G.border}` }}>
                <td style={{ padding:'6px 8px', fontWeight:600 }}>{b.name}</td>
                <td style={{ padding:'6px 8px' }}><Badge c={b.type==='subaccount'?G.purple:G.cyan} bg={(b.type==='subaccount'?G.purple:G.cyan)+'22'}>{b.type}</Badge></td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{b.deliveries||0}</td>
                <td style={{ padding:'6px 8px', textAlign:'right', color:G.green, fontWeight:600 }}>{b.won||0}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{b.avg_response_seconds ? `${Math.round(b.avg_response_seconds/60)}m` : '—'}</td>
                <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:700 }}>{fmtUsd((b.spent_cents||0)/100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    </div>

    {/* By source */}
    {(data.bySource||[]).length > 0 && (
      <div style={{ ...styles.card, padding:16 }}>
        <div style={{ fontWeight:700, marginBottom:10 }}>By Source</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {(data.bySource||[]).map((r,i) => (
            <Badge key={i} c={G.cyan} bg={G.cyan+'22'}>{r.source || 'unknown'} · {r.n}</Badge>
          ))}
        </div>
      </div>
    )}
  </div>;
}

function Kpi({ label, value, sub, deltaPct, color }) {
  return <div style={{ ...styles.card, padding:'14px 16px' }}>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>{label}</div>
    <div style={{ display:'flex', alignItems:'baseline', gap:8, marginTop:4 }}>
      <div style={{ fontSize:22, fontWeight:800, color: color || G.text }}>{value}</div>
      {deltaPct != null && <div style={{ fontSize:12, fontWeight:700, color: deltaPct>=0?G.green:G.red }}>
        {deltaPct >= 0 ? '↑' : '↓'} {Math.abs(deltaPct)}%
      </div>}
    </div>
    {sub && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{sub}</div>}
  </div>;
}

function MiniBarChart({ data }) {
  if (!data.length) return <div style={{ color:G.textMuted, fontSize:13, padding:20, textAlign:'center' }}>No lead activity in this window.</div>;
  const max = Math.max(...data.map(d => d.n), 1);
  return <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:80 }}>
    {data.map((d, i) => (
      <div key={i} title={`${d.day}: ${d.n}`} style={{
        flex:1, minWidth:4,
        height: `${(d.n / max) * 100}%`,
        background: G.cyan, borderRadius:'2px 2px 0 0',
        transition:'background .15s',
      }}/>
    ))}
  </div>;
}

function BreakdownCard({ title, rows }) {
  return <div style={{ ...styles.card, padding:14 }}>
    <div style={{ fontWeight:700, marginBottom:8 }}>{title}</div>
    {!rows.length ? <div style={{ color:G.textMuted, fontSize:13 }}>No data.</div> :
      rows.slice(0, 10).map((r,i) => <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
        <span>{r.label}</span>
        <span><strong>{r.value}</strong>{r.right ? ` · ${r.right}` : ''}</span>
      </div>)}
  </div>;
}

// ─── SUB-ACCOUNT BUYER VIEW ─────────────────────────────────────────────────
function SubAccountBuyerView({ toast, accountIndustry }) {
  const VERTICALS = visibleVerticals(accountIndustry);
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ verticals: [], zip_codes: '', max_leads_per_day: 20, max_bid_cents: 5000, preferred_pricing_models: ['exclusive','shared','per_booked','per_revenue'] });
  const [deliveries, setDeliveries] = useState([]);
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api('/api/lead-marketplace/pricing-preview').then(setPricing).catch(()=>{}); }, []);

  const loadBuyer = () => api('/api/lead-marketplace/buyers').then(d => {
    const first = (d.buyers || [])[0];
    setBuyer(first || null);
    if (first) api(`/api/lead-marketplace/my-deliveries`).then(r => setDeliveries(r.deliveries||[])).catch(()=>{});
  }).catch(()=>setBuyer(null)).finally(()=>setLoading(false));
  useEffect(() => { loadBuyer(); }, []);

  const toggleVertical = (v) => {
    const cur = form.verticals || [];
    setForm({ ...form, verticals: cur.includes(v) ? cur.filter(x=>x!==v) : [...cur, v] });
  };

  const enroll = async () => {
    if (!form.verticals.length) return toast('Pick at least one vertical', 'error');
    const zips = String(form.zip_codes).split(/[,\s]+/).filter(z => /^\d{5}$/.test(z));
    if (!zips.length) return toast('Add at least one 5-digit zip', 'error');
    if (!form.preferred_pricing_models.length) return toast('Pick at least one pricing model', 'error');
    try {
      await api('/api/lead-marketplace/my-buyer', {
        method: 'POST',
        body: {
          verticals: form.verticals,
          zip_codes: zips,
          max_leads_per_day: Number(form.max_leads_per_day) || 20,
          max_bid_cents: Number(form.max_bid_cents) || 0,
          preferred_pricing_models: form.preferred_pricing_models,
        },
      });
      toast('✓ Enrolled in Marketplace');
      setCreating(false); loadBuyer();
    } catch (e) { toast(e.message, 'error'); }
  };
  const togglePricingModel = (m) => {
    const cur = form.preferred_pricing_models || [];
    setForm({ ...form, preferred_pricing_models: cur.includes(m) ? cur.filter(x=>x!==m) : [...cur, m] });
  };

  const updateBuyer = async (patch) => {
    try { await api('/api/lead-marketplace/my-buyer', { method:'PATCH', body: patch }); loadBuyer(); toast('Updated'); }
    catch (e) { toast(e.message, 'error'); }
  };

  if (loading) return <Spinner sz={32}/>;

  return <div style={{ padding:'20px 24px', maxWidth:1100 }}>
    <div style={{ marginBottom:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>💰 Get New Leads</h1>
      <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
        Buy overflow + rank-and-rent leads that match your verticals + zips. Pay a small delivery fee + rev-share on close (no upfront cost for TradeOps accounts).
      </div>
    </div>

    {!buyer && !creating && (
      <EmptyState icon="💰" title="Not enrolled yet"
        body="Pick your verticals + zips, set a daily cap + bid. Leads that match get routed straight into your CRM the moment they land."
        ctaLabel="Enroll to Get Leads" onCta={()=>setCreating(true)}/>
    )}

    {(!buyer && creating) && (
      <div style={{ ...styles.card, padding:20 }}>
        <h3 style={{ margin:'0 0 12px', fontSize:16 }}>Enroll — Get Matched to Leads</h3>

        <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Verticals you serve</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
          {VERTICALS.map(v => {
            const on = form.verticals.includes(v);
            return <button key={v} onClick={()=>toggleVertical(v)}
              style={{ padding:'6px 12px', borderRadius:16, border:`1px solid ${on?G.purple:G.border}`, background: on?G.purpleDim:G.card, color: on?G.purple:G.text, fontSize:12, cursor:'pointer' }}>{v}</button>;
          })}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:14 }}>
          <Field label="Zip codes served (comma or space separated)">
            <Input value={form.zip_codes} onChange={e=>setForm({...form, zip_codes:e.target.value})} placeholder="33544, 33549, 33559"/>
          </Field>
          <Field label="Max leads per day">
            <Input type="number" value={form.max_leads_per_day} onChange={e=>setForm({...form, max_leads_per_day:e.target.value})}/>
          </Field>
          <Field label="Max bid per lead (cents)">
            <Input type="number" value={form.max_bid_cents} onChange={e=>setForm({...form, max_bid_cents:e.target.value})} placeholder="5000 = $50"/>
          </Field>
        </div>

        <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Pricing models you'll accept</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:8, marginBottom:14 }}>
          {PRICING_MODELS.map(m => {
            const on = form.preferred_pricing_models.includes(m.id);
            return <div key={m.id} onClick={()=>togglePricingModel(m.id)}
              style={{ cursor:'pointer', padding:12, borderRadius:10, border:`1px solid ${on?G.purple:G.border}`, background: on?G.purpleDim:G.card }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                <input type="checkbox" checked={on} readOnly/>
                <strong style={{ fontSize:13 }}>{m.label}</strong>
              </div>
              <div style={{ fontSize:11, color:G.textMuted, marginBottom:6 }}>{m.desc}</div>
              {pricing && <PricePreviewChips pricing={pricing} model={m.id}/>}
            </div>;
          })}
        </div>

        <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
          <Btn onClick={()=>setCreating(false)}>Cancel</Btn>
          <Btn v="primary" onClick={enroll}>Enroll</Btn>
        </div>
      </div>
    )}

    {buyer && <>
      <div style={{ ...styles.card, padding:20, marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
          <div>
            <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700 }}>Your Buyer Profile</div>
            <div style={{ fontSize:16, fontWeight:800, marginTop:2 }}>{buyer.name}</div>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <Badge c={buyer.active?G.green:G.textMuted} bg={(buyer.active?G.green:G.textMuted)+'22'}>{buyer.active?'Active':'Paused'}</Badge>
            <Btn sz="sm" onClick={()=>updateBuyer({ active: buyer.active?0:1 })}>{buyer.active?'⏸ Pause':'▶ Reactivate'}</Btn>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, marginBottom:12 }}>
          <MiniStat label="Delivered (all-time)" value={buyer.total_deliveries || 0} color={G.cyan}/>
          <MiniStat label="Won (rev-share)" value={buyer.leads_won_30d || 0} color={G.green}/>
          <MiniStat label="Credit balance" value={fmtUsd((buyer.credit_balance_cents||0)/100)} color={G.purple}/>
          <MiniStat label="Total spent" value={fmtUsd((buyer.total_spent_cents||0)/100)} color={G.orange}/>
        </div>
        <div style={{ fontSize:12, color:G.textMuted, marginBottom:6 }}>
          <strong>Verticals:</strong> {(buyer.verticals||[]).join(', ') || '—'}<br/>
          <strong>Zips:</strong> {(buyer.zip_codes||[]).slice(0,10).join(', ')}{(buyer.zip_codes||[]).length>10 ? ` +${(buyer.zip_codes||[]).length-10} more` : ''}<br/>
          <strong>Daily cap:</strong> {buyer.max_leads_per_day || 20} · <strong>Max bid:</strong> {fmtUsd((buyer.max_bid_cents||0)/100)}<br/>
          <strong>Pricing models:</strong> {(buyer.preferred_pricing_models||[]).map(m => PRICING_MODELS.find(x=>x.id===m)?.label || m).join(', ') || 'all'}
        </div>
        {pricing && <div style={{ marginTop:10, padding:12, background:G.input, borderRadius:8 }}>
          <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>Your pricing (as a {pricing.buyerType} · {(pricing.markups?.[pricing.buyerType]||1).toFixed(2)}× our cost)</div>
          <PriceTable pricing={pricing}/>
        </div>}
        <details style={{ marginTop:10 }}>
          <summary style={{ cursor:'pointer', fontSize:12, color:G.cyan }}>✏️ Edit profile</summary>
          <BuyerEditor buyer={buyer} onSave={updateBuyer} VERTICALS={VERTICALS}/>
        </details>
      </div>

      <div style={{ ...styles.card, padding:16 }}>
        <div style={{ fontSize:14, fontWeight:700, marginBottom:10 }}>Recent Deliveries</div>
        {deliveries.length === 0 ? (
          <div style={{ color:G.textMuted, fontSize:13, padding:14, textAlign:'center' }}>No leads delivered yet — the moment one lands in your verticals + zips, it appears here and hits your CRM.</div>
        ) : (
          <table style={{ width:'100%', fontSize:13 }}>
            <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
              <th style={{ textAlign:'left', padding:'6px 8px' }}>When</th>
              <th style={{ textAlign:'left', padding:'6px 8px' }}>Lead</th>
              <th style={{ textAlign:'left', padding:'6px 8px' }}>Vertical</th>
              <th style={{ textAlign:'left', padding:'6px 8px' }}>Category</th>
              <th style={{ textAlign:'right', padding:'6px 8px' }}>Charged</th>
              <th style={{ textAlign:'right', padding:'6px 8px' }}>Status</th>
            </tr></thead>
            <tbody>
              {deliveries.slice(0, 50).map(d => (
                <tr key={d.id} style={{ borderTop:`1px solid ${G.border}` }}>
                  <td style={{ padding:'6px 8px' }}>{new Date(d.created_at).toLocaleDateString()}</td>
                  <td style={{ padding:'6px 8px' }}>{d.first_name} {d.last_name}</td>
                  <td style={{ padding:'6px 8px' }}>{d.vertical}</td>
                  <td style={{ padding:'6px 8px' }}>{d.category}</td>
                  <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmtUsd((d.charged_cents||0)/100)}</td>
                  <td style={{ padding:'6px 8px', textAlign:'right' }}>{d.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>}
  </div>;
}

function PriceTable({ pricing }) {
  const prices = pricing.prices || {};
  const cats = Object.keys(prices);
  if (!cats.length) return null;
  return <table style={{ width:'100%', fontSize:12 }}>
    <thead><tr style={{ color:G.textMuted, fontSize:10, textTransform:'uppercase' }}>
      <th style={{ textAlign:'left', padding:'4px 6px' }}>Category</th>
      {PRICING_MODELS.map(m => <th key={m.id} style={{ textAlign:'right', padding:'4px 6px' }}>{m.label}</th>)}
    </tr></thead>
    <tbody>
      {cats.map(cat => <tr key={cat} style={{ borderTop:`1px solid ${G.border}` }}>
        <td style={{ padding:'4px 6px', fontWeight:600 }}>{cat}</td>
        {PRICING_MODELS.map(m => {
          const p = prices[cat]?.[m.id];
          return <td key={m.id} style={{ padding:'4px 6px', textAlign:'right' }}>{
            !p ? <span style={{ color:G.textMuted }}>—</span>
            : p.kind === 'revenue_share' ? <strong style={{ color:G.green }}>{p.pct}% rev</strong>
            : <strong>${(p.cents/100).toFixed(0)}</strong>
          }</td>;
        })}
      </tr>)}
    </tbody>
  </table>;
}

function PricePreviewChips({ pricing, model }) {
  const prices = pricing.prices || {};
  const cats = Object.keys(prices);
  return <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
    {cats.map(cat => {
      const p = prices[cat]?.[model];
      if (!p) return null;
      const text = p.kind === 'revenue_share' ? `${cat}: ${p.pct}%` : `${cat}: $${(p.cents/100).toFixed(0)}`;
      return <span key={cat} style={{ fontSize:10, padding:'2px 6px', borderRadius:8, background:G.input, color:G.text }}>{text}</span>;
    })}
  </div>;
}

function MiniStat({ label, value, color }) {
  return <div style={{ ...styles.card, padding:'10px 12px', background:G.input }}>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:18, fontWeight:800, marginTop:2, color: color || G.text }}>{value}</div>
  </div>;
}

function BuyerEditor({ buyer, onSave, VERTICALS = ALL_VERTICALS }) {
  const [f, setF] = React.useState({
    verticals: buyer.verticals || [],
    zip_codes: (buyer.zip_codes || []).join(', '),
    max_leads_per_day: buyer.max_leads_per_day || 20,
    max_bid_cents: buyer.max_bid_cents || 0,
    preferred_pricing_models: buyer.preferred_pricing_models?.length ? buyer.preferred_pricing_models : ['exclusive','shared','per_booked','per_revenue'],
  });
  const toggle = (v) => setF({ ...f, verticals: f.verticals.includes(v) ? f.verticals.filter(x=>x!==v) : [...f.verticals, v] });
  const toggleModel = (m) => setF({ ...f, preferred_pricing_models: f.preferred_pricing_models.includes(m) ? f.preferred_pricing_models.filter(x=>x!==m) : [...f.preferred_pricing_models, m] });
  return <div style={{ marginTop:14 }}>
    <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Verticals</div>
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
      {VERTICALS.map(v => {
        const on = f.verticals.includes(v);
        return <button key={v} onClick={()=>toggle(v)}
          style={{ padding:'4px 10px', borderRadius:14, border:`1px solid ${on?G.purple:G.border}`, background: on?G.purpleDim:G.card, color: on?G.purple:G.text, fontSize:11, cursor:'pointer' }}>{v}</button>;
      })}
    </div>
    <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Pricing models</div>
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
      {PRICING_MODELS.map(m => {
        const on = f.preferred_pricing_models.includes(m.id);
        return <button key={m.id} onClick={()=>toggleModel(m.id)}
          style={{ padding:'4px 10px', borderRadius:14, border:`1px solid ${on?G.cyan:G.border}`, background: on?G.cyan+'22':G.card, color: on?G.cyan:G.text, fontSize:11, cursor:'pointer' }}>{m.label}</button>;
      })}
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:10 }}>
      <Field label="Zips"><Input value={f.zip_codes} onChange={e=>setF({...f, zip_codes:e.target.value})}/></Field>
      <Field label="Daily cap"><Input type="number" value={f.max_leads_per_day} onChange={e=>setF({...f, max_leads_per_day:e.target.value})}/></Field>
      <Field label="Max bid (¢)"><Input type="number" value={f.max_bid_cents} onChange={e=>setF({...f, max_bid_cents:e.target.value})}/></Field>
    </div>
    <div style={{ textAlign:'right' }}>
      <Btn v="primary" onClick={()=>onSave({
        verticals: f.verticals,
        zip_codes: String(f.zip_codes).split(/[,\s]+/).filter(z => /^\d{5}$/.test(z)),
        max_leads_per_day: Number(f.max_leads_per_day)||20,
        max_bid_cents: Number(f.max_bid_cents)||0,
        preferred_pricing_models: f.preferred_pricing_models,
      })}>Save</Btn>
    </div>
  </div>;
}
